import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

import { memberDetailPath, membersListToApiQuery, type MembersListState } from './members-list-state';

interface AdjacentResponse {
  prevId: string | null;
  nextId: string | null;
  position: number;
  total: number;
}

export const MemberDetailNavigation = ({
  memberId,
  listState,
}: {
  memberId: string;
  listState: MembersListState;
}) => {
  const { t } = useTranslation('people');
  const navigate = useNavigate();
  const churchId = useChurchStore((state) => state.church?.id ?? null);

  const adjacent = useQuery({
    queryKey: queryKeys.list(churchId, 'people/members/adjacent', { memberId, ...listState }),
    queryFn: () => {
      const params = new URLSearchParams(membersListToApiQuery({ ...listState, page: 1 }));
      return api.get<AdjacentResponse>(`/people/members/${memberId}/adjacent?${params.toString()}`);
    },
    enabled: Boolean(churchId && memberId),
  });

  if (!adjacent.data || adjacent.data.total <= 1) return null;

  const { prevId, nextId, position, total } = adjacent.data;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-body-sm text-fg-secondary">
        {t('members.navigation.position', {
          position: formatNumber(position),
          total: formatNumber(total),
        })}
      </span>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!prevId}
          aria-label={t('members.navigation.previous')}
          onClick={() => {
            if (prevId) void navigate(memberDetailPath(prevId, listState));
          }}
        >
          <ChevronLeft className="size-4" aria-hidden />
          {t('members.navigation.previous')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!nextId}
          aria-label={t('members.navigation.next')}
          onClick={() => {
            if (nextId) void navigate(memberDetailPath(nextId, listState));
          }}
        >
          {t('members.navigation.next')}
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
};
